"use client";

import { useState, useEffect } from "react";
import { Bot } from "lucide-react";

const allInsightsPool = [
  // Google Ads signal
  "Found 7 products with Google Ads ROAS down >30% in the last 28 days.",
  "Bamboo Water Bottle: ROAS dropped from 4.2× to 2.6× on Google Ads.",
  "Linen Tote Bag: paid ROAS now 1.8× — was 3.1× four weeks ago.",
  "Cork Yoga Block: Google Ads ROAS at 2.1×, cost-per-click up 18%.",
  // Search Console signal
  "Same 7 products show organic clicks up 40–120% in Search Console.",
  "Bamboo Water Bottle: organic clicks +78% month-over-month (Search Console).",
  "Linen Tote Bag: organic CTR up 55%, impressions rising steadily.",
  "Cork Yoga Block: organic clicks +112% — now ranking top-3 organically.",
  // Cross-source conclusions
  "These products convert 2.3× better on organic than on paid channels.",
  "Pattern: Search Console intent is strongest where Google Ads ROAS is weakest.",
  "Shifting spend on these 7 products could free ~$3,200/month.",
  "Paid spend on high-organic products is masking true channel performance.",
  // Actions
  "Action: Reduce Google Ads spend on the 7 organically strong products.",
  "Action: Reallocate ~$3,200/mo to products with no organic traction yet.",
  "Action: Pause ads on Bamboo Water Bottle first — lowest ROAS, best organic rank.",
  "Action: Monitor Search Console weekly — if organic rank drops, restore budget.",
];

const capabilityZones = [
  "Joining Google Ads × Search Console...",
  "Scanning ROAS trends across ad campaigns...",
  "Cross-referencing paid vs. organic signals...",
  "Matching ad spend with organic click data...",
  "Identifying budget-shift opportunities...",
  "Analyzing channel overlap for 7 products...",
  "Modeling reallocation impact on spend...",
];

export function AnalysisPlayback() {
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);
  const [insights, setInsights] = useState<string[]>([]);
  const [capabilityIndex, setCapabilityIndex] = useState(0);

  useEffect(() => {
    // Shuffle and pick a start point
    const shuffled = [...allInsightsPool].sort(() => Math.random() - 0.5);
    setInsights(shuffled);
  }, []);

  useEffect(() => {
    if (insights.length === 0) return;

    // Phase: Finished typing, now lingering
    if (!reverse && subIndex === insights[index].length) {
      const timeout = setTimeout(() => {
        setReverse(true);
      }, 2500); // Linger for 2.5 seconds
      return () => clearTimeout(timeout);
    }

    // Phase: Finished deleting, move to next
    if (reverse && subIndex === 0) {
      const timeout = setTimeout(() => {
        setReverse(false);
        const nextIndex = (index + 1) % insights.length;
        setIndex(nextIndex);
        
        // Every 3 insights, change the capability zone
        if (nextIndex % 3 === 0) {
          setCapabilityIndex(prev => (prev + 1) % capabilityZones.length);
        }
      }, 400); // Slightly more breathing room
      return () => clearTimeout(timeout);
    }

    // Phase: Typing or Deleting
    const timeout = setTimeout(() => {
      setSubIndex((prev) => prev + (reverse ? -1 : 1));
    }, reverse ? 10 : 25); // 25ms typing, 10ms deleting

    return () => clearTimeout(timeout);
  }, [subIndex, index, reverse, insights]);

  if (insights.length === 0) return null;

  const currentLine = insights[index].substring(0, subIndex);
  const isAction = insights[index].startsWith("Action:");


  return (
    <div className="w-full h-[280px] bg-slate-900/40 rounded-2xl border border-slate-800/50 p-8 md:p-10 font-mono text-base md:text-xl flex flex-col overflow-hidden relative shadow-inner">
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent pointer-events-none z-10" />
      
      <div className="relative z-0 flex flex-col space-y-10 pt-2">
        <div className="text-purple-400/90 font-bold flex items-center gap-4 h-9">
          <div className="flex h-9 w-9 items-center justify-center rounded-full shadow-lg bg-gradient-to-br from-purple-500 to-pink-500 shadow-purple-500/30 flex-shrink-0">
             <Bot className="h-5 w-5 text-white" />
          </div>
          <span className="tracking-tight transition-all duration-500 text-lg md:text-2xl">
            {capabilityZones[capabilityIndex]}
          </span>
        </div>
        
        <div className="flex items-start gap-4 min-h-[5rem]">
          <span className={`${isAction ? 'text-purple-500' : 'text-purple-500/30'} select-none mt-1.5 flex-shrink-0 text-2xl font-bold w-6 text-center`}>
            {isAction ? '★' : '›'}
          </span>
          <span className={`leading-relaxed tracking-tight block ${isAction ? 'text-purple-300 font-medium italic' : 'text-slate-300'} max-w-[90%]`}>
            {currentLine}
            <span className="inline-block w-2.5 h-6 ml-2 bg-purple-500/50 animate-[pulse_1s_infinite] align-middle" />
          </span>
        </div>
      </div>

      <div className="absolute bottom-10 left-8 md:left-10 flex items-center gap-3 text-sm uppercase tracking-[0.3em] text-slate-600 font-bold select-none z-20">
        <div className="flex gap-1">
          <div className="w-1 h-1 rounded-full bg-purple-500/40 animate-pulse" />
          <div className="w-1 h-1 rounded-full bg-purple-500/20 animate-pulse delay-75" />
          <div className="w-1 h-1 rounded-full bg-purple-500/10 animate-pulse delay-150" />
        </div>
        Real-time Data Processing
      </div>
    </div>
  );
}




